<template>
  <div class="project-communication">
    <AccessBlock v-if="isTasksSectionVisible" :id="PROJECT_TASKS_BLOCK">
      <div class="accordion" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-communication-tasks
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-tasks mr-2"></i>{{ $t('tasks') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-communication-tasks" role="tabpanel" visible>
            <div class="accordion-content px-2 py-2">
              <ProjectTasks :project="project" />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>

    <AccessBlock
      v-if="showToGuest('employerCommunication', guestVisibleSections)"
      :id="PROJECT_EMPLOYER_COMMUNICATION_BLOCK"
    >
      <div class="accordion" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-communication-employer
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-comments mr-2"></i>{{ $t('communication_with_employer') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-communication-employer" role="tabpanel">
            <div class="accordion-content px-2 py-2">
              <ProjectEmployerCommunication
                v-if="employerCommunicationProject"
                :blockId="PROJECT_EMPLOYER_COMMUNICATION_BLOCK"
                :project="employerCommunicationProject"
              />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>

    <AccessBlock v-if="isPostsSectionVisible" :id="PROJECT_POSTS_BLOCK">
      <div class="accordion" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-communication-posts
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-sticky-note mr-2"></i>{{ $t('posts') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-communication-posts" role="tabpanel">
            <div class="accordion-content px-2 py-2">
              <ProjectPosts v-if="postsProject" :project="postsProject" />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>

    <AccessBlock v-if="isEmailSectionVisible" :id="PROJECT_EMAIL_BLOCK">
      <div class="accordion" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-communication-email
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-envelope mr-2"></i>{{ $t('email') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-communication-email" role="tabpanel">
            <div class="accordion-content px-2 py-2">
              <ProjectEmail
                v-if="emailProject"
                :project="emailProject"
                :selectedAppointment="selectedAppointment"
              />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>
  </div>
</template>

<script>
import AccessBlock from '@/components/Access/AccessBlock.vue';
import { mapGetters } from 'vuex';
import ProjectEmployerCommunication from '@/blocks/project/ProjectEmployerCommunication.vue';
import ProjectEmail from '@/blocks/project/ProjectEmail.vue';
import ProjectPosts from '@/blocks/project/ProjectPosts.vue';
import ProjectTasks from '@/blocks/project/ProjectTasks.vue';
import { showToGuest } from '@/helpers/userCapabilities';
import {
  PROJECT_EMAIL_BLOCK,
  PROJECT_EMPLOYER_COMMUNICATION_BLOCK,
  PROJECT_POSTS_BLOCK,
  PROJECT_TASKS_BLOCK,
} from '@/navigation/accessNodes';

/** Number of the unsaved project (ProjectEdit `_new`); it has no blocks yet. */
const NEW_PROJECT_NUMBER = '_new';

export default {
  components: {
    AccessBlock,
    ProjectEmail,
    ProjectEmployerCommunication,
    ProjectTasks,
    ProjectPosts,
  },
  props: {
    project: { type: Object },
    selectedAppointment: { type: Object, default: null },
  },
  data() {
    return {
      PROJECT_TASKS_BLOCK,
      PROJECT_EMPLOYER_COMMUNICATION_BLOCK,
      PROJECT_POSTS_BLOCK,
      PROJECT_EMAIL_BLOCK,
      // Guest permissions
      guestVisibleSections: {
        employerCommunication: true,
        tasks: true,
        email: false,
        posts: false,
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['isOrganizationRoleNavigationEnabled']),
    /**
     * Switch on, only the block level counts (AccessBlock); switch off, the guest condition of the
     * section, as before the block.
     */
    isTasksSectionVisible() {
      return (
        this.isOrganizationRoleNavigationEnabled ||
        this.showToGuest('tasks', this.guestVisibleSections)
      );
    },
    /**
     * Switch on, only the block level counts (AccessBlock); switch off, guests don't see the
     * posts, as before the block.
     */
    isPostsSectionVisible() {
      return (
        this.isOrganizationRoleNavigationEnabled ||
        this.showToGuest('posts', this.guestVisibleSections)
      );
    },
    /**
     * Switch on, only the block level counts (AccessBlock); switch off, guests don't see the
     * email section, as before the block.
     */
    isEmailSectionVisible() {
      return (
        this.isOrganizationRoleNavigationEnabled ||
        this.showToGuest('email', this.guestVisibleSections)
      );
    },
    /**
     * Data of the block project-employer-communication: the excerpt from `Project.blocks`, null
     * when not loaded or denied. The unsaved project has no blocks; the block gets the project
     * itself and its values go with `createProject`.
     */
    employerCommunicationProject() {
      if (this.project.number === NEW_PROJECT_NUMBER) {
        return this.project;
      }
      return this.project.blocks?.employerCommunication ?? null;
    },
    /**
     * Data of the block project-posts, null when not loaded or denied. The unsaved project has
     * no blocks; the block gets the project itself and shows its hint.
     */
    postsProject() {
      if (this.project.number === NEW_PROJECT_NUMBER) {
        return this.project;
      }
      return this.project.blocks?.posts ?? null;
    },
    /**
     * Data of the block project-email, null when not loaded or denied. The unsaved project has
     * no blocks; the block gets the project itself and shows its hint.
     */
    emailProject() {
      if (this.project.number === NEW_PROJECT_NUMBER) {
        return this.project;
      }
      return this.project.blocks?.email ?? null;
    },
  },
  methods: {
    showToGuest,
  },
};
</script>
